import React from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import styled from 'styled-components';

import BulkSelectToggleButton from 'components/lib/BulkSelectToggleButton';
import Input from 'components/lib/Input';
import { isMysteryShopAdmin, isVendorShopAdmin } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import CommunitiesListCompanyFilter from './CommunitiesListCompanyFilter';
import { useSettingsContext } from './SettingsContext';

const Container = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
`;

const LeftFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;

const Filters = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledSelect = styled(Select)<{ $filterWidth: number }>`
  margin-right: 10px;

  &&& {
    .ant-select-selector {
      width: ${({ $filterWidth }) => $filterWidth}px;
      padding: 0 10px;
      border-radius: 0; /* selector squared */
      border: solid 1px var(--line-gray);

      .ant-select-selection-item {
        font-size: 14px;
        padding: 0 !important;
      }
    }

    &:hover,
    &:focus {
      .ant-select-selector {
        border: solid 1px var(--line-gray) !important;
      }
    }
  }

  /* dropdown itself */
  .ant-select-dropdown {
    border-radius: 0 !important; /* dropdown squared */
    padding: 0;
  }

  /* dropdown list items */
  .ant-select-item {
    padding: 0 10px !important; /* reduce padding */
  }

  .ant-select-item {
    border-radius: 0;
    line-height: 38px;
  }

  .ant-select-item:not(:last-child) {
    border-bottom: 1px solid var(--line-gray);
  }
`;

type Props = {
  selectPopupWidth?: number;
  statusFilterWidth?: number;
  hideFilters?: boolean;
  toggleMultipleSelection: () => void;
  setSearchTerm: (value: string) => void;
  setStatus: (value: number | null) => void;
  setPage: (value: number) => void;
};

const SettingsToolbar = ({
  hideFilters = false,
  selectPopupWidth,
  statusFilterWidth = 148,
  toggleMultipleSelection,
  setSearchTerm,
  setStatus,
  setPage,
}: Props) => {
  const { filterOptions, isMultipleSelection, status, searchTerm, companiesFilter } = useSettingsContext();
  const { currentUser } = useAppSelector((state) => state.auth);

  const handleChange = (event: any) => {
    const search = event.target.value;
    setSearchTerm(search);
    setPage(1);
  };

  const handleFilter = (value: any) => {
    setStatus(value);
    setPage(1);
  };

  const isCompanyScopedAdmin = Boolean(
    currentUser?.isSuperuser || isVendorShopAdmin(currentUser) || isMysteryShopAdmin(currentUser)
  );
  const disableBulkSelect = isCompanyScopedAdmin && companiesFilter.length == 0;

  return (
    <Container>
      <LeftFilters>
        <BulkSelectToggleButton
          active={isMultipleSelection}
          disabled={disableBulkSelect}
          tooltipTitle={disableBulkSelect ? 'Filter by company to enable multi select' : undefined}
          onClick={toggleMultipleSelection}
        />
        {isCompanyScopedAdmin && <CommunitiesListCompanyFilter setPage={setPage}></CommunitiesListCompanyFilter>}
      </LeftFilters>
      <Filters>
        {!hideFilters && (
          <StyledSelect
            $filterWidth={statusFilterWidth}
            options={filterOptions}
            value={status}
            onChange={handleFilter}
            popupMatchSelectWidth={selectPopupWidth}
            listHeight={360}
            dropdownStyle={{ width: 'max-content', minWidth: statusFilterWidth }}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          />
        )}

        <Input
          value={searchTerm}
          prefix={<SearchOutlined />}
          style={{ width: 400 }}
          onChange={handleChange}
          placeholder="Search communities"
        />
      </Filters>
    </Container>
  );
};

export default SettingsToolbar;
